import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { IoCloudUploadOutline } from "react-icons/io5";
import { PiX } from "react-icons/pi";
import * as XLSX from "xlsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import BluePrimaryButton from "../../common/button/bluePrimaryButton";
import GreenButton from "../../common/button/greenButton";
import CtkViewer from "../../common/CtkViewer";
import xlsxIcon from "../../assets/images/uploads/xlsx.png";
import { getSystemVariablesApi, listTemplatesApi, previewUploadApi, sendUploadApi, errorMessage } from "./api";
import { CHANNEL_LABELS, CHANNELS, usedVariables } from "./variables";
import VariableInputs from "./VariableInputs";
import { CountTile, EmailTable, labelSx, useFeedback } from "./common";

const ACCEPT = ".xlsx,.xls,.csv";
const MAX_SIZE = 5 * 1024 * 1024;
const boxSx = { backgroundColor: "#fafbff", border: "1px solid #e3e5f5", borderRadius: "8px", p: 2 };

// Send by upload: a template to the candidates in an Excel/CSV file, on SMS / WhatsApp / Email, after Check File.
export default function SendUpload() {
  const navigate = useNavigate();
  const feedback = useFeedback();
  const [searchParams] = useSearchParams();
  const project = Cookies.get("mhet_mst_project");

  const [templates, setTemplates] = useState([]);
  const [systemVariables, setSystemVariables] = useState([]);
  const [templateid, setTemplateid] = useState(searchParams.get("templateid") || "");
  const [selected, setSelected] = useState({ sms: false, whatsapp: false, email: false });
  const [values, setValues] = useState({});
  const [courseid, setCourseid] = useState("");
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [check, setCheck] = useState(null);
  const [checkKey, setCheckKey] = useState(""); // the request the check was made for
  const [checkError, setCheckError] = useState("");
  const [checking, setChecking] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getSystemVariablesApi().then((v) => setSystemVariables(v || [])).catch(() => {});
    listTemplatesApi({ active: true })
      .then((list) => setTemplates(list || []))
      .catch((err) => feedback.failed(errorMessage(err, "Could not load templates")));
    // eslint-disable-next-line
  }, []);

  const template = templates.find((t) => String(t.templateid) === String(templateid));
  // A new template: tick every channel it has.
  useEffect(() => {
    setValues({});
    setCheck(null);
    setResult(null);
    setSelected(Object.fromEntries(CHANNELS.map((c) => [c, !!template?.[c]])));
    // eslint-disable-next-line
  }, [templateid, templates.length]);

  const channels = CHANNELS.filter((c) => selected[c] && template?.[c]);
  // Values are keyed by label (the service accepts labels).
  const used = template ? [...new Map(channels.flatMap((c) => usedVariables(template, c, systemVariables)).map((v) => [v.name, v])).values()] : [];
  const sendVars = used.filter((v) => v.source === "send").map((v) => ({ ...v, name: v.label }));
  const fileVars = used.filter((v) => v.source === "file");
  const fileColumns = ["Candidate ID", "Course ID", ...fileVars.map((v) => v.label)];

  const request = { templateid, channels, file, courseid, values: Object.fromEntries(Object.entries(values).filter(([, v]) => String(v).trim() !== "")) };
  const requestKey = useMemo(
    () => JSON.stringify({ ...request, file: file ? `${file.name}:${file.size}:${file.lastModified}` : null }),
    [templateid, selected, file, courseid, values] // eslint-disable-line
  );
  const missing = check?.missingValues || [];
  const checkIsCurrent = check && checkKey === requestKey;
  const canSend = checkIsCurrent && missing.length === 0 && Object.values(check.channels).some((c) => c.recipients > 0) && !sending;

  const downloadSample = () => {
    const example = { "Candidate ID": 12345, "Course ID": 23 };
    fileVars.forEach((v) => { example[v.label] = ""; });
    const sheet = XLSX.utils.json_to_sheet([example], { header: fileColumns });
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Recipients");
    XLSX.writeFile(book, `${template?.code || "send"}_recipients.xlsx`);
  };

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.(xlsx|xls|csv)$/i.test(f.name)) return setFileError("Upload an .xlsx, .xls or .csv file");
    if (f.size > MAX_SIZE) return setFileError("The file is larger than 5 MB");
    setFileError("");
    setFile(f);
    setResult(null);
  };

  const runCheck = async () => {
    if (courseid && !/^\d+$/.test(courseid)) return setCheckError("Default Course ID must be a number");
    setChecking(true);
    setCheckError("");
    try {
      const res = await previewUploadApi(request);
      setCheck(res.data);
      setCheckKey(requestKey);
    } catch (err) {
      setCheck(null);
      setCheckError(errorMessage(err, "Could not check the file"));
    }
    setChecking(false);
  };

  const send = async () => {
    setConfirm(false);
    setSending(true);
    feedback.loader(true);
    try {
      const res = await sendUploadApi(request);
      setResult(res);
      feedback.success(res.message);
    } catch (err) {
      feedback.failed(errorMessage(err, "Could not start sending"));
    }
    feedback.loader(false);
    setSending(false);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography sx={labelSx}>Template (Event) *</Typography>
            <Select fullWidth size="small" displayEmpty value={templateid} onChange={(e) => setTemplateid(String(e.target.value))}>
              <MenuItem value="" disabled>Select a template</MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.templateid} value={String(t.templateid)}>
                  {t.eventname} <Typography component="span" variant="caption" sx={{ ml: 1, color: "#777" }}>({CHANNELS.filter((c) => t[c]).map((c) => CHANNEL_LABELS[c]).join(", ")})</Typography>
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {template && (
            <>
              <Grid item xs={12}>
                <Typography sx={labelSx}>Send by</Typography>
                {CHANNELS.map((c) => (
                  <FormControlLabel key={c} disabled={!template[c]} control={<Checkbox checked={!!(selected[c] && template[c])} onChange={(e) => setSelected({ ...selected, [c]: e.target.checked })} />} label={CHANNEL_LABELS[c]} />
                ))}
              </Grid>
              <Grid item xs={12}>
                <Box sx={boxSx}>
                  <Typography variant="body2"><b>File columns:</b> {fileColumns.join(", ")}</Typography>
                </Box>
              </Grid>
              {sendVars.length > 0 && (
                <Grid item xs={12}>
                  <VariableInputs
                    title="Values for this send"
                    variables={sendVars}
                    values={values}
                    errors={Object.fromEntries(sendVars.filter((v) => checkIsCurrent && missing.includes(v.label)).map((v) => [v.name, "Required"]))}
                    onChange={(n, v) => setValues({ ...values, [n]: v })}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography sx={labelSx}>Default Course ID</Typography>
                <TextField fullWidth size="small" value={courseid} onChange={(e) => setCourseid(e.target.value.trim())} helperText="Used for rows without a Course ID" />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={labelSx}>Upload File *</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                {!file ? (
                  <Box sx={{ border: "1px dashed gray", maxWidth: 420, minHeight: 44, display: "flex", alignItems: "center" }}
                    onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }} onDragOver={(e) => e.preventDefault()}>
                    <input type="file" id="sendrecipientsfile" accept={ACCEPT} style={{ width: "0.1px", height: "0.1px", opacity: 0, position: "absolute", zIndex: -1 }} onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
                    <label htmlFor="sendrecipientsfile" style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                      &nbsp; <IoCloudUploadOutline size={22} /> &nbsp; Drag or drop your file, or <span style={{ color: "blue" }}>&nbsp;browse</span>
                    </label>
                  </Box>
                ) : (
                  <Box sx={{ maxWidth: 420, height: 45, border: "1px solid #D5D5D5", display: "flex", alignItems: "center", gap: 1, px: 1 }}>
                    <img src={xlsxIcon} style={{ width: 30 }} alt="file" />
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                    <PiX style={{ cursor: "pointer" }} onClick={() => { setFile(null); setCheck(null); }} />
                  </Box>
                )}
                <GreenButton title="Download Sample File" handleSubmit={downloadSample} />
                </Box>
                {fileError && <Typography variant="caption" sx={{ color: "red" }}>{fileError}</Typography>}
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Button variant="outlined" sx={{ borderRadius: "40px" }} disabled={!file || channels.length === 0 || checking} onClick={runCheck}>
                  {checking ? "Checking..." : checkIsCurrent ? "Check Again" : "Check File"}
                </Button>
                <BluePrimaryButton title={sending ? "Sending..." : "Send"} disabled={!canSend} handleSubmit={() => setConfirm(true)} />
                {check && !checkIsCurrent && <Typography variant="caption" sx={{ color: "#b56200" }}>Something changed — check the file again before sending.</Typography>}
                {checkIsCurrent && missing.length > 0 && <Typography variant="caption" sx={{ color: "red" }}>Fill in {missing.join(", ")} and check again.</Typography>}
              </Grid>
            </>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12} md={5}>
        <Box sx={boxSx}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>How it works</Typography>
          <Typography variant="body2" component="div" sx={{ color: "#444", lineHeight: 1.7 }}>
            1. Select the template and the channels, and download the sample file.<br />
            2. Fill one row per candidate: <b>Candidate ID</b>, <b>Course ID</b>{fileVars.length ? <> and {fileVars.map((v) => <b key={v.name}>{v.label} </b>)}</> : null}.<br />
            3. Name, course, email address and mobile number come from the database.<br />
            4. <b>Check File</b> shows how many will receive each message, the rows that will be skipped and why, and a sample — nothing is sent.<br />
            5. <b>Send</b> runs in the background — follow it under <b>Send History</b>.
          </Typography>
        </Box>
      </Grid>

      {checkError && <Grid item xs={12}><Alert severity="error">{checkError}</Alert></Grid>}
      {check && (
        <Grid item xs={12}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <CountTile label="Rows in file" value={check.totalRows} />
            <CountTile label="Not usable (all channels)" value={check.fileSkipped} color={check.fileSkipped ? "rgba(181,98,0,1)" : undefined} />
          </Box>
          <Grid container spacing={2}>
            {CHANNELS.filter((c) => check.channels[c]).map((c) => {
              const p = check.channels[c];
              return (
                <Grid item xs={12} md={c === "email" ? 12 : 6} key={c}>
                  <Box sx={boxSx}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>{CHANNEL_LABELS[c]}</Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Chip size="small" color="success" label={`${p.recipients} will receive`} />
                        {p.skipped > 0 && <Chip size="small" label={`${p.skipped} skipped`} />}
                      </Box>
                    </Box>
                    {p.reasons.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        {p.reasons.map((r) => <Typography key={r.reason} variant="body2" sx={{ color: "#555" }}>• {r.reason}: <b>{r.count}</b></Typography>)}
                      </Box>
                    )}
                    {p.sample ? (
                      <>
                        <Typography variant="caption" sx={{ color: "#666" }}>Sample for row {p.sampleFor.row} (Candidate {p.sampleFor.candidateid}) · {p.sampleFor.to}</Typography>
                        {c === "email" ? (
                          <>
                            <Typography sx={{ fontWeight: 600, mt: 1 }}>{p.sample.subject}</Typography>
                            <Box sx={{ border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "#fff", p: 2, mt: 1, maxHeight: 320, overflowY: "auto" }}><CtkViewer content={p.sample.html} /></Box>
                          </>
                        ) : (
                          <Box sx={{ border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", p: 1.5, mt: 1, whiteSpace: "pre-wrap" }}>{p.sample.text}</Box>
                        )}
                        {c === "sms" && <Typography variant="caption" sx={{ color: "#666" }}>{p.sample.length} characters · {p.sample.parts} SMS · DLT {p.sample.dltid}</Typography>}
                        {c === "whatsapp" && <Typography variant="caption" sx={{ color: "#666" }}>Template {p.sample.templatename} · values: {p.sample.params.join(" | ") || "none"}</Typography>}
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ color: "#666" }}>No row of the file can receive {CHANNEL_LABELS[c]}.</Typography>
                    )}
                    {p.skippedRows.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Skipped rows {p.skipped > p.skippedRows.length ? `(first ${p.skippedRows.length} of ${p.skipped})` : ""}</Typography>
                        <EmailTable columns={[{ name: "Row", accessor: "row" }, { name: "Candidate ID", accessor: "candidateid" }, { name: "Reason", accessor: "reason", align: "left" }]} rows={p.skippedRows} />
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      )}

      {result && (
        <Grid item xs={12}>
          <Alert severity="success" sx={{ mb: 1 }}>{result.message}</Alert>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {result.data.jobs.map((j) => (
              <Button key={j.jobid} variant="outlined" size="small" onClick={() => navigate(`/${project}/send-history?page=detail&jobid=${j.jobid}`)}>
                {j.channel}: job {j.jobid} · {j.recipients} to send · {j.skipped} skipped
              </Button>
            ))}
          </Box>
        </Grid>
      )}

      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle>Send now?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}><b>{template?.eventname}</b> to the candidates in <b>{file?.name}</b></Typography>
          {channels.map((c) => <Typography key={c} variant="body2">• {CHANNEL_LABELS[c]} to {check?.channels?.[c]?.recipients} candidates</Typography>)}
          <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>Messages cannot be recalled once sent.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => setConfirm(false)}>Cancel</Button>
          <BluePrimaryButton title="Send" handleSubmit={send} />
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
